<template>
  <div>
    <h1 class="title is-size-4-mobile">
      {{ $t("Location") }} {{ projectName }}
    </h1>

    <field-horizontal :label="$t('Location name')">
      <input-field
        v-model="projectNameMod"
        @focus="projectNameMod_onFocus"
        :errors="projectNameMod_errorsIfTouched"
        maxlength="33"
      />
      <button
        slot="right"
        class="button is-primary"
        :disabled="!projectNameMod_valid || !projectNameMod_dirty"
        @click="saveProjectName"
      >
        {{ $t("Save") }}
      </button>
      <field-message
        slot="message"
        :success="$t(projectNameSavedSuccessMsg)"
        :failure="$t(projectNameSavedFailureMsg)"
      />
    </field-horizontal>

    <field-horizontal :label="$t('Currency')">
      <input-field
        v-model="projectCurrencyMod"
        @focus="projectCurrencyMod_onFocus"
        :errors="projectCurrencyMod_errorsIfTouched"
        maxlength="6"
      />
      <button
        slot="right"
        class="button is-primary"
        :disabled="!projectCurrencyMod_valid || !projectCurrencyMod_dirty"
        @click="saveProjectCurrency"
      >
        {{ $t("Save") }}
      </button>
      <field-message
        slot="message"
        :success="$t(projectCurrencySavedSuccessMsg)"
        :failure="$t(projectCurrencySavedFailureMsg)"
      />
    </field-horizontal>

    <field-horizontal :label="$t('Default image')" :isFullWidth="true">
      <image-field
        class="small-image"
        :mealImageUrl="projectDefaultImage.imageUrl"
        :mealOriginUrl="projectDefaultImage.originUrl"
        :cropData="projectDefaultImage.crop"
        accept="image/*"
        @upload="uploadImage"
        @change="changeImage"
        :editMode="true"
        :loading="uploadingProjectImage"
        :imageTitle="$t('Default image')"
      />
      <field-message
        slot="message"
        :success="$t(projectImageSavedSuccessMsg)"
        :failure="$t(projectImageSavedFailureMsg)"
      />
    </field-horizontal>

    <hr />

    <field-horizontal :label="$t('REST API')">
      <switch-field
        class="switch-field"
        value="projectRestApi"
        @change="toggleRestApi"
      />
      <field-message
        slot="message"
        :success="$t(projectRestApiSavedSuccessMsg)"
        :failure="$t(projectRestApiSavedFailureMsg)"
      />
    </field-horizontal>
    <read-and-copy-field
      v-if="projectRestApi"
      label="Meals URL"
      :url="restApiUrl + 'meals.json'"
    />
    <read-and-copy-field
      v-if="projectRestApi"
      label="Offer URL"
      :url="restApiUrl + 'offers.json'"
    />
    <read-and-copy-field
      v-if="projectRestApi"
      label="Currency URL"
      :url="restApiUrl + 'currency.json'"
    />
    <read-and-copy-field
      v-if="projectRestApi"
      label="Scheduling URL"
      :url="restApiUrl + 'offersScheduling.json'"
    />
    <read-and-copy-field
      v-if="projectRestApi"
      label="Configuration URL"
      :url="restApiUrl + 'offersSchedulingConf.json'"
    />

    <hr />

    <field-horizontal :label="$t('Danger zone')" v-if="!isDemoProject">
      <button class="button is-danger" @click="openRemoveProjectConfimation">
        {{ $t("Delete location") }}
      </button>
    </field-horizontal>

    <modal-confirmation
      :is-open.sync="removingProject"
      :title="$t('Are you sure you want to delete this location?')"
      :confirmText="$t('Delete')"
      @confirm="removeProject"
    >
      {{
        $t(
          "Once location {0} is deleted, you will not be able to recover it!",
          projectName
        )
      }}
    </modal-confirmation>
  </div>
</template>

<script>
import { state, actions } from "@/store"
import { resetTimeout } from "@/utils"
import imageService from "@/service/image-service"
import { validator, size, maxSize, required, nonEmpty } from "@/validator"
import {
  InputField,
  FieldHorizontal,
  SwitchField,
  FieldMessage,
  ReadAndCopyField,
  ImageField
} from "@/components/form"
import { ModalConfirmation } from "@/components/modal"

const MSG_TIMEOUT_MS = 3000

export default {
  name: "location-settings",
  mixins: [validator],
  components: {
    InputField,
    FieldHorizontal,
    ReadAndCopyField,
    SwitchField,
    FieldMessage,
    ModalConfirmation,
    ImageField
  },
  data() {
    return {
      projectNameMod: "",
      projectNameSavedSuccessMsg: undefined,
      projectNameSavedFailureMsg: undefined,
      projectCurrencyMod: "",
      projectCurrencySavedSuccessMsg: undefined,
      projectCurrencySavedFailureMsg: undefined,
      projectRestApiSavedSuccessMsg: undefined,
      projectRestApiSavedFailureMsg: undefined,
      removingProject: false,
      uploadingProjectImage: false,
      projectImageSavedSuccessMsg: undefined,
      projectImageSavedFailureMsg: undefined
    }
  },
  computed: {
    isDemoProject() {
      return state.project.id == "DEMO"
    },
    projectName() {
      return state.project.name
    },
    projectCurrency() {
      return state.project.data.currency
    },
    projectDefaultImage() {
      return state.project.data.defaultImage
    },
    projectRestApi() {
      return !!state.project.data.restApi
    },
    restApiUrl() {
      return actions.getRestApiUrl()
    }
  },
  watch: {
    projectName: {
      immediate: true,
      handler(value) {
        this.projectNameMod_reset(value)
      }
    },
    projectCurrency: {
      immediate: true,
      handler(value) {
        this.projectCurrencyMod_reset(value)
      }
    }
  },
  validations: {
    projectNameMod: [required, size(3, 32), nonEmpty],
    projectCurrencyMod: [maxSize(4)]
  },
  methods: {
    saveProjectName() {
      this.projectNameSavedFailureMsg = undefined
      this.projectNameSavedSuccessMsg = undefined
      actions
        .setProjectName(this.projectNameMod)
        .then(() => {
          this.projectNameSavedSuccessMsg = "Saved"
          resetTimeout(
            "saveProjectName",
            () => {
              this.projectNameSavedSuccessMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          this.projectNameSavedFailureMsg = "Failed to change location name."
          this.$logger.error("save-project-name-error", e)
        })
    },
    saveProjectCurrency() {
      this.projectCurrencySavedFailureMsg = undefined
      this.projectCurrencySavedSuccessMsg = undefined
      actions
        .setProjectCurrency(this.projectCurrencyMod)
        .then(() => {
          this.projectCurrencySavedSuccessMsg = "Saved"
          resetTimeout(
            "saveProjectCurrency",
            () => {
              this.projectCurrencySavedSuccessMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          this.projectCurrencySavedFailureMsg =
            "Failed to change currency setting."
          this.$logger.error("save-project-currency-error", e)
        })
    },
    toggleRestApi() {
      this.projectRestApiSavedFailureMsg = undefined
      this.projectRestApiSavedSuccessMsg = undefined
      actions
        .setProjectRestApi(!this.projectRestApi)
        .then(() => {
          this.projectRestApiSavedSuccessMsg = "Saved"
          resetTimeout(
            "saveProjectRestApi",
            () => {
              this.projectRestApiSavedSuccessMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          this.projectRestApiSavedFailureMsg =
            "Failed to change REST API setting."
          this.$logger.error("save-project-REST-error", e)
        })
    },
    removeProject() {
      this.closeRemoveProjectConfirmation()
      this.$router.push("/manager")
      actions
        .removeCurrentProject()
        .catch(actions.addError("remove-project-error"))
    },
    openRemoveProjectConfimation() {
      this.removingProject = true
    },
    closeRemoveProjectConfirmation() {
      this.removingProject = false
    },
    changeImage(cropData) {
      this.projectImageSavedFailureMsg = undefined
      this.projectImageSavedSuccessMsg = undefined
      this.uploadingProjectImage = true
      const url = this.projectDefaultImage.originUrl
      if (!url || !cropData) {
        this.$logger.error("no-image-crop")
        return
      }
      imageService
        .cropImage(url, cropData)
        .then(imageUrls => {
          const suffix = "&d=" + Date.now()
          const projectImage = {
            imageUrl: imageUrls.imageUrl + suffix,
            originUrl: imageUrls.originUrl,
            crop: cropData
          }
          return actions.setProjectDefaultImage(state.project.id, projectImage)
        })
        .then(() => {
          this.uploadingProjectImage = false
          this.projectImageSavedSuccessMsg = "Saved"
          resetTimeout(
            "saveProjectImage",
            () => {
              this.projectImageSavedSuccessMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          this.uploadingProjectImage = false
          this.$logger.error("edit-image-error", e)
          switch (e && e.message) {
            case "offline":
              this.projectImageSavedFailureMsg =
                "Image edit failed, missing network connection"
              break
            default:
              this.projectImageSavedFailureMsg = "Image upload failed"
              break
          }
        })
    },
    uploadImage(file) {
      this.projectImageSavedFailureMsg = undefined
      this.projectImageSavedSuccessMsg = undefined
      if (file === undefined) {
        this.$logger.info("no image given")
        return
      }
      this.uploadingProjectImage = true
      imageService
        .uploadImage(file.imageRef, file)
        .then(imageUrls => {
          const projectImage = {
            imageUrl: imageUrls.imageUrl,
            originUrl: imageUrls.originUrl
          }
          return actions.setProjectDefaultImage(state.project.id, projectImage)
        })
        .then(() => {
          this.uploadingProjectImage = false
          this.projectImageSavedSuccessMsg = "Saved"
          resetTimeout(
            "saveProjectImage",
            () => {
              this.projectImageSavedSuccessMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          this.uploadingProjectImage = false
          this.$logger.error("upload-image-error", e)
          switch (e && e.message) {
            case "offline":
              this.projectImageSavedFailureMsg =
                "Image upload failed, missing network connection"
              break
            case "fileTooBig":
              this.projectImageSavedFailureMsg =
                "Image upload failed, file is too large (max 3MB)"
              break
            default:
              this.projectImageSavedFailureMsg = "Image upload failed"
              break
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.switch-field {
  position: absolute;
  bottom: 0;
}
.small-image {
  max-width: 446px;
}
</style>
